/**
 * ===================================================================================
 * 📁 경로: /frontend/src/pages/MainPage.js
 * 📌 프로그램명: 메인 페이지 컴포넌트
 * 🧩 개요:
 * - AI 닥터큐 서비스의 메인 화면으로, 사용자가 건강검진 PDF 파일을 업로드하고
 * AI 분석을 요청하는 핵심 기능을 제공합니다.
 * - 파일 업로드, 분석 요청, 실시간 진행 상황 표시, 최종 리포트 렌더링,
 * 중복 파일 처리, 암호화 PDF 처리 등 사용자 상호작용의 전 과정을 관리합니다.
 * ===================================================================================
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios"; // ✨ Axios는 단일 파일 업로드 요청에 여전히 유용합니다.
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { markdownSanitizeOptions } from "../utils/markdownSanitizeOptions";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/useAuth";
import AnalysisProgress from "../components/AnalysisProgress";
import DuplicateFoundModal from "../components/DuplicateFoundModal";
import MessageModal from "../components/MessageModal";
import {
  authHeader,
  getToken,
  refreshAccessToken,
  isAppEnvironment,
  isTokenExpired,
  requestTokenFromApp,
} from "../utils/auth";
import { AUTH_SERVICE_UNAVAILABLE, fetchPatientList } from "../utils/patientApi";
import { nhisGetLatestCollection } from "../utils/nhisApi";

/**
 * [Why]
 * AI 닥터큐 서비스의 핵심 사용자 경험을 제공하는 메인 페이지 컴포넌트입니다.
 * 사용자는 이 페이지를 통해 자신의 건강 데이터를 업로드하고, 복잡한 백엔드 AI 분석
 * 파이프라인의 결과를 실시간으로 확인하며, 최종적으로 개인 맞춤형 건강 리포트를
 * 받아볼 수 있습니다.
 *
 * [How]
 * - `useState` 훅을 사용하여 파일, 로딩 상태, 결과, 에러 등 컴포넌트의 모든 상태를 관리합니다.
 * - `useEffect` 훅을 사용하여 컴포넌트 마운트/언마운트 시 EventSource 연결을 관리하고,
 * 인증 상태 변화를 감지하여 UI를 동기화합니다.
 * - `startStream` 함수는 백엔드(`/api/v1/analysis/stream`) API를 호출하고,
 * Server-Sent Events (SSE)를 통해 실시간으로 진행 상황을 수신하여 상태를 업데이트합니다.
 * - `fetch` API를 사용하여 비동기 통신을 처리하며, 다양한 응답(성공, 중복, 오류, 암호화)에
 * 따라 적절한 UI(로딩 바, 모달, 에러 메시지)를 렌더링합니다.
 * - 최종 결과는 `ReactMarkdown` 라이브러리를 사용하여 안전하게 HTML로 렌더링됩니다.
 *
 * @component
 * @returns {JSX.Element} MainPage 컴포넌트의 JSX 엘리먼트.
 */
function MainPage() {
  const auth = useAuth();
  // [내 데이터 분석 기능 노출 제어]
  // - React는 빌드 시점에 REACT_APP_* 환경변수를 번들에 고정합니다.
  // - 따라서 "내 데이터로 분석하기" 기능은 REACT_APP_ENABLE_DIRECT_ANALYSIS=true 일 때만 노출합니다.
  const enableDirectAnalysis =
    String(process.env.REACT_APP_ENABLE_DIRECT_ANALYSIS || "").toLowerCase() ===
    "true";
  const location = useLocation();
  const currentPath = `${location.pathname}${location.search}`;
  const basePath = location.pathname.startsWith("/app") ? "/app" : "";
  const isApp = isAppEnvironment();
  // 분석 방식: pdf | direct
  // 앱 환경은 항상 'direct' 고정, 웹은 기존 로직 유지
  const [analysisMode, setAnalysisMode] = useState(
    isApp ? "direct" : location.state?.analysisMode || "pdf",
  );
  const [file, setFile] = useState(null);
  const [result, setResult] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState([]);
  const [summaryPreview, setSummaryPreview] = useState("");
  const eventSourceRef = useRef(null);
  const navigate = useNavigate();

  // 중복 파일 발견 시 모달 표시를 위한 상태
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [duplicateReportId, setDuplicateReportId] = useState(null);

  // 분석 완료 후 리포트 피드백 UI 관리를 위한 상태
  const [reportId, setReportId] = useState(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(null); // 'helpful' | 'needs_improvement'
  const [feedbackDate, setFeedbackDate] = useState(null);
  const [feedbackError, setFeedbackError] = useState(null);

  // 인증 상태 (메인에서 모달은 관리하지 않음)
  // 환자 목록 상태 (직접 분석용)
  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [patientLoadError, setPatientLoadError] = useState("");
  const [patientDropdownOpen, setPatientDropdownOpen] = useState(false);

  // 앱 전용: 마이데이터 수집 상태
  const [collectionStatus, setCollectionStatus] = useState(null);
  const [isLoadingCollectionStatus, setIsLoadingCollectionStatus] =
    useState(false);
  const [latestAppReport, setLatestAppReport] = useState(null);
  const [isLoadingLatestReport, setIsLoadingLatestReport] = useState(false);

  // 항상 최신 토큰 상태를 평가하기 위한 헬퍼
  const isAuthed = auth.isAuthenticated;
  const authReady = !auth.isBooting;
  const userNo = auth.userNo || "";

  // 암호화된 PDF 처리를 위한 모달 관련 상태
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [pendingEncryptedFile, setPendingEncryptedFile] = useState(null);
  const [pdfPasswordInput, setPdfPasswordInput] = useState("");
  const [pdfPasswordCached, setPdfPasswordCached] = useState("");
  const [pdfPasswordError, setPdfPasswordError] = useState("");

  // 메시지 모달 상태 (에러/알림용)
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const normalizeProgress = useCallback((items) => {
    if (!Array.isArray(items)) return items;
    const hasBeyondStep = items.some(
      (p) => typeof p?.step === "number" && p.step > 1,
    );
    if (!hasBeyondStep) return items;
    return items.map((p) => {
      if (p?.step === 1 && p.status !== "completed" && p.status !== "failed") {
        const message =
          typeof p?.message === "string"
            ? p.message
                .replace("시작합니다.", "완료되었습니다.")
                .replace("시작합니다", "완료되었습니다")
            : p?.message;
        return {
          ...p,
          status: "completed",
          message,
        };
      }
      return p;
    });
  }, []);

  // 컴포넌트가 사라질 때(언마운트) SSE 연결이 남아있지 않도록 정리합니다.
  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const currentEventSource = eventSourceRef.current;
      if (currentEventSource) {
        console.log("컴포넌트 언마운트: EventSource 연결을 닫습니다.");
        currentEventSource.close();
      }
    };
  }, []);

  // [직접분석] 환자 목록 조회 (라디오 direct 선택 시)
  useEffect(() => {
    if (isApp) return; // 앱 환경에서는 collectionStatus로 대체
    // direct 모드가 아닐 때는 건너뜀
    if (analysisMode !== "direct") return;
    if (!authReady) return;
    // 비로그인 시 모달 유도만 하고 목록 조회 안 함
    if (!isAuthed) {
      setPatients([]);
      setSelectedPatientId("");
      setIsLoadingPatients(false);
      const reason = auth.actions.getSnapshot().lastError;
      setPatientLoadError(
        ["patients-unauthorized", "refresh-rejected", "refresh-missing", "verify-unauthorized"].includes(reason)
          ? "로그인이 만료되었습니다. 다시 로그인해 주세요."
          : "로그인이 필요합니다. 로그인 후 다시 시도해 주세요.",
      );
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL
      ? String(process.env.REACT_APP_API_URL).replace(/\/$/, "")
      : "";
    const url = `${apiUrl}/api/v1/patients`;

    let active = true;
    const fetchPatients = async () => {
      setIsLoadingPatients(true);
      setPatientLoadError("");
      try {
        const list = await fetchPatientList(url, { isCurrent: () => active });
        if (!active || list === null) return;
        // 중복 환자 제거: 한국 4Keys(이름/생년월일/성별/전화번호) 기준으로 식별
        const deduped = [];
        const seen = new Set();
        for (const p of list) {
          const name = (p.name || p.patient_name || "").trim();
          const birth = (p.birth_date || "").trim();
          const gender = (p.gender || "").trim();
          const telecom = (p.telecom || p.phone || p.telecom_text || "").trim();
          const key = `${name}|${birth}|${gender}|${telecom}`;
          if (seen.has(key)) continue;
          seen.add(key);
          deduped.push(p);
        }

        setPatients(deduped);
        if (deduped.length === 1) {
          // 환자 1명인 경우 자동 선택
          const only = deduped[0];
          const pid = only.patient_id || only.id || "";
          setSelectedPatientId(pid ? String(pid) : "");
        } else {
          setSelectedPatientId("");
        }
      } catch (e) {
        if (!active) return;
        // [로그] 환자 목록 조회 실패. UI에서 메시지 표시
        console.error("환자 목록 조회 오류:", e);
        setPatientLoadError(e?.message || "환자 목록을 불러오지 못했습니다.");
        setPatients([]);
        setSelectedPatientId("");
      } finally {
        if (active) setIsLoadingPatients(false);
      }
    };

    fetchPatients();
    return () => { active = false; };
  }, [analysisMode, auth.actions, auth.userNo, authReady, enableDirectAnalysis, isApp, isAuthed]);

  // 커스텀 환자 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    if (!patientDropdownOpen) return;
    const handler = () => setPatientDropdownOpen(false);
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [patientDropdownOpen]);

  // [앱 전용] 수집 상태 로드
  useEffect(() => {
    if (!isApp) return;
    setIsLoadingCollectionStatus(true);
    nhisGetLatestCollection()
      .then((data) => {
        const patient = data.patients?.[0] ?? { hasCollection: false };
        setCollectionStatus(patient);
        if (patient.hasCollection && patient.stdPatientId) {
          setSelectedPatientId(String(patient.stdPatientId));
        }
      })
      .catch(() => setCollectionStatus({ hasCollection: false }))
      .finally(() => setIsLoadingCollectionStatus(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // [앱 전용] 최신 리포트 상태 로드
  useEffect(() => {
    if (!isApp) return;
    if (!authReady) return;
    if (!userNo || !isAuthed) {
      setLatestAppReport(null);
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL
      ? String(process.env.REACT_APP_API_URL).replace(/\/$/, "")
      : "";

    const fetchLatestReport = async () => {
      setIsLoadingLatestReport(true);
      try {
        const params = new URLSearchParams();
        params.set("fields", "report_id,created_at,title,is_latest,summary");
        params.set("include_superseded", "false");
        params.set("limit", "1");
        params.set("offset", "0");

        const resp = await axios.get(
          `${apiUrl}/api/v1/users/${userNo}/reports?${params.toString()}`,
          { headers: authHeader() },
        );
        const data = resp?.data;
        const list = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
            ? data
            : [];
        setLatestAppReport(list[0] ?? null);
      } catch {
        setLatestAppReport(null);
      } finally {
        setIsLoadingLatestReport(false);
      }
    };

    fetchLatestReport();
  }, [auth.accessToken, authReady, isApp, isAuthed, userNo]); // eslint-disable-line react-hooks/exhaustive-deps

  // 모달 열릴 때마다 비밀번호 입력란/에러 초기화
  useEffect(() => {
    if (passwordModalOpen) {
      setPdfPasswordInput("");
      setPdfPasswordError("");
    }
  }, [passwordModalOpen]);

  const hasCollectedData = !!collectionStatus?.hasCollection;
  const hasLatestReport = !!latestAppReport?.report_id;
  const latestReportId = latestAppReport?.report_id ?? null;
  const reportStatus = hasLatestReport ? "reported" : "empty";

  const reportStatusTitle =
    reportStatus === "reported"
      ? "이미 생성된 건강 리포트가 있어요"
      : "건강 리포트를 아직 만들지 않았어요";

  const reportStatusDescription =
    reportStatus === "reported"
      ? "기존 리포트를 볼 수 있고, 원하면 최신 의료 데이터를 다시 가져와 새 리포트를 만들 수 있어요."
      : "간편인증을 마치면 건강 리포트에 필요한 의료 정보를 바로 가져올 수 있어요.";

  const primaryAppCtaLabel =
    reportStatus === "reported"
      ? "새 리포트 만들어볼까요"
      : "건강 리포트 만들어볼까요";

  const handlePrimaryAppCta = () => {
    navigate(basePath + "/mydata/connect/consent");
  };

  const handleSecondaryAppCta = () => {
    if (!hasCollectedData) return;
    navigate(basePath + "/analysis/dashboard");
  };

  const handleLatestReportView = () => {
    if (!latestReportId) return;
    navigate(basePath + `/reports/${latestReportId}`);
  };

  const requireLogin = useCallback((message) => {
    setError("");
    setIsErrorModalOpen(false);

    const intent = {
      kind: isApp ? "app" : "web",
      path: currentPath,
      reason: "main-page-auth-required",
      createdAt: Date.now(),
    };

    if (isApp) {
      auth.actions.rememberIntent(intent);
      navigate("/app/login", { replace: true });
      return;
    }

    auth.actions.openLoginModal({
      message,
      intent,
    });
  }, [auth.actions, currentPath, isApp, navigate]);

  /**
   * [Why]
   * 사용자가 파일 선택 input을 클릭했을 때, 로그인 상태를 확인하여 비로그인 사용자의
   * 파일 업로드를 막는 UI/UX 가드 역할을 합니다.
   *
   * [How]
   * `useAuth()` 기반 인증 상태를 확인하고, 비로그인 상태일 경우
   * 파일 선택 창이 뜨는 기본 동작을 막고(`preventDefault`), 공통 인증 모달을 엽니다.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event - 파일 input의 change 이벤트 객체.
   */
  const handleFileChange = (event) => {
    if (!authReady) {
      event.target.value = "";
      return;
    }

    // 비로그인 상태에서의 안전장치 (이중 가드)
    if (!isAuthed) {
      event.target.value = "";
      requireLogin("로그인이 필요합니다. 로그인 후 파일을 업로드해 주세요.");
      return;
    }
    setFile(event.target.files[0]);
    // 새 파일 선택 시 이전 암호는 무효화
    setPdfPasswordCached("");
  };

  /**
   * [Why]
   * 백엔드 AI 분석 파이프라인을 시작하고, Server-Sent Events(SSE)를 통해
   * 실시간으로 결과를 수신하는 핵심 비동기 함수입니다.
   *
   * [How]
   * 1.  모든 관련 상태(로딩, 에러, 결과 등)를 초기화합니다.
   * 2.  `FormData`를 생성하여 선택된 파일과 분석 옵션(재분석 여부, 비밀번호 등)을 담습니다.
   * 3.  `fetch` API를 사용하여 백엔드 `/api/v1/analysis/stream` 엔드포인트에 POST 요청을 보냅니다.
   * 4.  응답이 성공적이면, 응답 본문을 스트리밍으로 읽어 SSE 메시지를 파싱합니다.
   * 5.  수신된 이벤트 타입('progress', 'duplicate_found', 'aidd-complete', 'error' 등)에 따라
   * React 상태를 업데이트하여 UI를 실시간으로 변경합니다.
   * 6.  백엔드에서 암호화 오류(400)를 반환하면, 비밀번호 입력 모달을 띄웁니다.
   *
   * @param {boolean} [forceReanalyze=false] - 중복 파일에 대해 강제 재분석을 요청할지 여부.
   * @param {string} [pdfPassword] - 암호화된 PDF 파일의 비밀번호.
   */
  const startStream = async (forceReanalyze = false, pdfPassword) => {
    // 상태 초기화
    setIsLoading(true);
    setError("");
    setIsErrorModalOpen(false);
    setResult("");
    setProgress([]);
    setSummaryPreview("");
    setIsDuplicate(false);
    setDuplicateReportId(null);

    // 업로드 요청을 보내기 전에 Step 1 processing을 프런트에서 표시
    setProgress((prev) => [
      {
        step: 1,
        status: "processing",
        message: forceReanalyze
          ? "중복 파일로 확인되어 새로 분석을 시작합니다."
          : "PDF 업로드 및 분석을 시작합니다.",
      },
    ]);

    const formData = new FormData();
    formData.append("file", file);
    if (forceReanalyze) formData.append("force_reanalyze", "true");
    if (pdfPassword) formData.append("pdf_password", pdfPassword);

    // [중요] 운영에서 REACT_APP_API_URL 미주입 시 localhost로 호출되는 문제를 방지합니다.
    //        기본값을 빈 문자열로 두면 `/api/...`가 현재 도메인으로 호출됩니다.
    const apiUrl = process.env.REACT_APP_API_URL
      ? String(process.env.REACT_APP_API_URL).replace(/\/$/, "")
      : "";
    const url = `${apiUrl}/api/v1/analysis/stream`;

    try {
      const headers = {
        ...(forceReanalyze ? { "X-Force-Reanalyze": "true" } : {}),
        ...authHeader(),
      };
      const response = await fetch(url, {
        method: "POST",
        body: formData,
        headers,
      });

      if (!response.ok) {
        // 암호화/비밀번호 오류인 경우 모달로 전환
        let detail = "";
        try {
          const j = await response.json();
          detail = j.detail || "";
        } catch (_) {}
        const lower = (detail || "").toLowerCase();
        if (
          response.status === 400 &&
          (lower.includes("암호화된 pdf") ||
            lower.includes("비밀번호") ||
            lower.includes("password"))
        ) {
          // 현재 파일을 보존하고 모달 오픈
          setIsLoading(false);
          setError(""); // 메인 화면 에러는 제거하고 모달에서만 안내
          setIsErrorModalOpen(false);
          setPasswordModalOpen(true);
          setPendingEncryptedFile(file);
          setPdfPasswordError(
            detail || "암호화된 PDF입니다. 비밀번호를 입력해 주세요.",
          );
          setPdfPasswordInput(""); // 모달 표시 직전 입력값 초기화
          // 기존에 잘못 저장되었을 수 있는 캐시 초기화 (재입력 유도)
          setPdfPasswordCached("");
          return; // 스트림 처리 중단
        }
        throw new Error(detail || `서버 오류: ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let buffer = "";
      let stopRequested = false;

      const processStream = async () => {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            if (!result) setIsLoading(false);
            break;
          }
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n\n");
          buffer = lines.pop();

          for (const line of lines) {
            if (!line.startsWith("data:") && !line.startsWith("event:")) continue;
            const eventTypeMatch = line.match(/^event: (.*)/);
            const dataMatch = line.match(/data: (.*)/);
            let eventType = eventTypeMatch
              ? eventTypeMatch[1].trim()
              : "message";
            const dataString = dataMatch ? dataMatch[1].trim() : "{}";
            try {
              const jsonData = JSON.parse(dataString);
              if (
                eventType === "message" &&
                jsonData &&
                typeof jsonData === "object" &&
                jsonData.event
              ) {
                eventType = jsonData.event;
              }

              if (eventType === "aidd-complete") {
                setResult(jsonData.final_explanation || "");
                if (
                  typeof jsonData.recommendation_overview === "string" &&
                  jsonData.recommendation_overview.trim()
                ) {
                  setSummaryPreview(jsonData.recommendation_overview);
                }
                setIsLoading(false);
                if (jsonData.report_id) {
                  setReportId(jsonData.report_id);
                }
              } else if (eventType === "duplicate_found") {
                const rid =
                  (jsonData && jsonData.data && jsonData.data.report_id) ||
                  jsonData.report_id;
                if (rid) {
                  setIsLoading(false);
                  stopRequested = true;
                  setIsDuplicate(true);
                  setDuplicateReportId(rid);
                } else {
                  setError(
                    "중복 파일로 확인되었으나 report_id를 찾지 못했습니다.",
                  );
                  setIsErrorModalOpen(true);
                  setIsLoading(false);
                  stopRequested = true;
                }
              } else if (eventType === "duplicate_not_linked") {
                const msg =
                  (jsonData && jsonData.data && jsonData.data.message) ||
                  jsonData.message ||
                  "중복 파일이지만 기존 리포트를 찾지 못했습니다.";
                setError(msg);
                setIsErrorModalOpen(true);
                setIsLoading(false);
                stopRequested = true;
              } else if (eventType === "error") {
                setError(jsonData.message || "알 수 없는 오류가 발생했습니다.");
                setIsErrorModalOpen(true);
                setIsLoading(false);
              } else if (eventType === "heartbeat") {
                // no-op
              } else {
                setProgress((prev) => {
                  const stepExists = prev.some((p) => p.step === jsonData.step);
                  let next;
                  if (stepExists) {
                    next = prev.map((p) =>
                      p.step === jsonData.step ? jsonData : p,
                    );
                  } else {
                    next = [...prev, jsonData].sort(
                      (a, b) => (a.step || 0) - (b.step || 0),
                    );
                  }
                  return normalizeProgress(next);
                });
              }
            } catch (e) {
              console.error(
                "SSE 메시지 파싱 오류:",
                e,
                "Original Data:",
                `"${dataString}"`,
              );
            }
          }

          if (stopRequested) {
            try {
              await reader.cancel();
            } catch (_) {}
            break;
          }
        }
      };
      processStream();
    } catch (err) {
      console.error("분석 요청 오류:", err);
      setError(
        err.message ||
          "분석을 시작하는 중 오류가 발생했습니다. 네트워크를 확인해주세요.",
      );
      setIsErrorModalOpen(true);
      setIsLoading(false);
    }
  };

  /**
   * [Why]
   * 사용자가 '분석 요청' 버튼을 클릭했을 때 실행되는 이벤트 핸들러입니다.
   *
   * [How]
   * `event.preventDefault()`로 폼의 기본 제출 동작을 막고, 로그인 상태와 파일 선택
   * 여부를 검증한 후 `startStream` 함수를 호출하여 분석 파이프라인을 시작합니다.
   *
   * @param {React.FormEvent<HTMLFormElement>} event - 폼 제출 이벤트 객체.
   */
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!authReady) {
      return;
    }

    // 비로그인 시 분석 시작을 막고 로그인 유도
    if (!isAuthed) {
      requireLogin("로그인이 필요합니다. 로그인 후 분석을 진행해 주세요.");
      return;
    }

    // direct 모드: 데이터가 없으면 마이데이터 연동 플로우로 이동, 있으면 바로 분석
    if (analysisMode === "direct") {
      // 앱: collectionStatus 기준 / 웹: patients 배열 기준으로 데이터 보유 여부 판단
      const hasData = isApp
        ? collectionStatus?.hasCollection
        : patients.length > 0;
      if (!hasData) {
        navigate(basePath + "/mydata/connect/consent");
        return;
      }
      await runDirectAnalysis();
      return;
    }

    // PDF 모드: 파일 필수
    if (!file) {
      setError("분석할 파일을 선택해주세요.");
      setIsErrorModalOpen(true);
      return;
    }

    // 토큰 갱신 로직: 앱 환경과 웹 환경 구분
    try {
      const token = auth.accessToken;

      // 앱 환경: 토큰이 만료된 경우에만 앱에 갱신 요청
      if (isAppEnvironment()) {
        if (token && isTokenExpired(token)) {
          const newToken = await requestTokenFromApp();
          if (!newToken) {
            // 조용히 로그인 페이지로 이동 (사용자에게 에러 메시지 표시 안 함)
            window.location.href = "/app/login";
            return;
          }
        }
      }
      // 웹 환경: 기존 로직대로 무조건 갱신 시도
      else {
        // [안전장치] 세션 만료/토큰 무효 시 로그인 유도 (DB 초기화 등)
        const ok = await refreshAccessToken();
        if (!ok || !isAuthed) {
          if (getToken()) {
            setPatientLoadError(AUTH_SERVICE_UNAVAILABLE);
            return;
          }
          requireLogin("세션이 만료되었습니다. 다시 로그인해 주세요.");
          return;
        }
      }
    } catch (_) {
      requireLogin("세션이 만료되었습니다. 다시 로그인해 주세요.");
      return;
    }

    await startStream(false);
  };

  // 표준 데이터(이미 적재된 std_*) 기반 분석 호출 - SSE 스트림
  const startDirectAnalysis = async () => {
    // 상태 초기화
    setIsLoading(true);
    setError("");
    setIsErrorModalOpen(false);
    setResult("");
    setProgress([
      {
        step: 1,
        status: "processing",
        message: "표준 데이터로 분석을 시작합니다.",
      },
    ]);
    setSummaryPreview("");
    setIsDuplicate(false);
    setDuplicateReportId(null);

    const apiUrl = process.env.REACT_APP_API_URL
      ? String(process.env.REACT_APP_API_URL).replace(/\/$/, "")
      : "";
    const url = `${apiUrl}/api/v1/analysis/stream-direct`;

    // [추가] 환자 선택값 검증
    // - [Why] 환자를 선택했는데 다른 환자의 데이터로 분석되는 사고를 방지합니다.
    // - [How] 환자가 2명 이상이면 선택값을 필수로 요구하고, 선택된 std_patient_id를 서버로 전달합니다.
    // - [Side Effect] 기존에는 선택 없이도 분석이 진행되었으나, 다중 환자 환경에서는 명시적 선택을 강제합니다.
    if (patients.length > 1 && !selectedPatientId) {
      setError("건강분석 리포트가 필요하신 분을 먼저 선택해 주세요.");
      setIsErrorModalOpen(true);
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeader(),
        },
        body: JSON.stringify({
          std_patient_id: selectedPatientId ? Number(selectedPatientId) : null,
        }),
      });

      if (!response.ok || !response.body) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(detail.detail || `서버 오류: ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let stopRequested = false;

      const processStream = async () => {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            if (!result) setIsLoading(false);
            break;
          }
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n\n");
          buffer = lines.pop();

          lines.forEach((line) => {
            if (!line.startsWith("data:") && !line.startsWith("event:")) return;
            const eventTypeMatch = line.match(/^event: (.*)/);
            const dataMatch = line.match(/data: (.*)/);
            let eventType = eventTypeMatch
              ? eventTypeMatch[1].trim()
              : "message";
            const dataString = dataMatch ? dataMatch[1].trim() : "{}";
            try {
              const jsonData = JSON.parse(dataString);
              if (
                eventType === "message" &&
                jsonData &&
                typeof jsonData === "object" &&
                jsonData.event
              ) {
                eventType = jsonData.event;
              }

              if (eventType === "aidd-complete") {
                setResult(jsonData.final_explanation || "");
                if (
                  typeof jsonData.recommendation_overview === "string" &&
                  jsonData.recommendation_overview.trim()
                ) {
                  setSummaryPreview(jsonData.recommendation_overview);
                }
                setIsLoading(false);
                if (jsonData.report_id) {
                  setReportId(jsonData.report_id);
                }
              } else if (eventType === "error") {
                setError(jsonData.message || "알 수 없는 오류가 발생했습니다.");
                setIsErrorModalOpen(true);
                setIsLoading(false);
              } else if (eventType === "heartbeat") {
                // no-op
              } else {
                setProgress((prev) => {
                  const stepExists = prev.some((p) => p.step === jsonData.step);
                  let next;
                  if (stepExists) {
                    next = prev.map((p) =>
                      p.step === jsonData.step ? jsonData : p,
                    );
                  } else {
                    next = [...prev, jsonData].sort(
                      (a, b) => (a.step || 0) - (b.step || 0),
                    );
                  }
                  return normalizeProgress(next);
                });
              }
            } catch (e) {
              console.error(
                "SSE 메시지 파싱 오류:",
                e,
                "Original Data:",
                `"${dataString}"`,
              );
            }
          });

          if (stopRequested) {
            try {
              await reader.cancel();
            } catch (_) {}
            break;
          }
        }
      };

      processStream();
    } catch (err) {
      setError(
        err?.message ||
          String(err) ||
          "표준 데이터 분석 중 오류가 발생했습니다.",
      );
      setIsErrorModalOpen(true);
      setIsLoading(false);
    }
  };

  // 토큰 체크 후 startDirectAnalysis() 호출. handleSubmit과 autoStart useEffect 양쪽에서 재사용.
  const runDirectAnalysis = async () => {
    if (!authReady) {
      return;
    }

    if (!isAuthed) {
      requireLogin("로그인이 필요합니다. 로그인 후 분석을 진행해 주세요.");
      return;
    }
    try {
      const token = auth.accessToken;
      if (isAppEnvironment()) {
        if (token && isTokenExpired(token)) {
          const newToken = await requestTokenFromApp();
          if (!newToken) {
            requireLogin("세션이 만료되었습니다. 다시 로그인해 주세요.");
            return;
          }
        }
      } else {
        const ok = await refreshAccessToken();
        if (!ok || !isAuthed) {
          if (getToken()) {
            setPatientLoadError(AUTH_SERVICE_UNAVAILABLE);
            return;
          }
          requireLogin("세션이 만료되었습니다. 다시 로그인해 주세요.");
          return;
        }
      }
    } catch (_) {
      requireLogin("세션이 만료되었습니다. 다시 로그인해 주세요.");
      return;
    }
    await startDirectAnalysis();
  };

  /**
   * [Why]
   * 암호화된 PDF의 비밀번호를 사용자가 입력하고 '확인'을 눌렀을 때 실행되는 핸들러입니다.
   *
   * [How]
   * 입력된 비밀번호와 함께 보류 중이던 파일을 사용하여 `startStream` 함수를 다시 호출합니다.
   * 성공적인 분석을 위해 입력된 비밀번호는 상태(`pdfPasswordCached`)에 저장합니다.
   */
  const handlePasswordConfirm = async () => {
    if (!pdfPasswordInput) {
      setPdfPasswordError("비밀번호를 입력해 주세요.");
      return;
    }
    if (!pendingEncryptedFile) {
      setPasswordModalOpen(false);
      return;
    }
    // 보류된 파일을 현재 파일로 설정 후 재시도
    setFile(pendingEncryptedFile);
    setPasswordModalOpen(false);
    setPdfPasswordError("");
    // 입력한 암호를 캐시에 저장하여 재분석 시 재사용
    setPdfPasswordCached(pdfPasswordInput);
    await startStream(false, pdfPasswordInput);
  };

  const handlePasswordCancel = () => {
    setPasswordModalOpen(false);
    setPdfPasswordError("");
    setPdfPasswordInput("");
    setPendingEncryptedFile(null);
    // 사용자가 취소하면 캐시도 초기화
    setPdfPasswordCached("");
  };

  const handleFileClick = (e) => {
    if (!authReady) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (!isAuthed) {
      e.preventDefault();
      e.stopPropagation();
      requireLogin("로그인이 필요합니다. 로그인 후 파일을 업로드해 주세요.");
    }
  };

  /**
   * [Why]
   * AI 리포트 하단에 있는 피드백 버튼('도움이 되었어요' 등)이 클릭되었을 때 실행됩니다.
   * 이 기능은 AI 모델의 성능을 평가하고 개선하기 위한 '자기개선' 원칙을 지원합니다.
   *
   * [How]
   * `fetch`를 사용하여 `/api/v1/reports/{reportId}/feedback` 엔드포인트에 POST 요청을
   * 보냅니다. 중복 제출을 방지하고, API 호출 결과에 따라 UI를 업데이트합니다.
   *
   * @param {'helpful' | 'needs_improvement'} rating - 사용자가 선택한 평가 값.
   */
  const handleFeedback = async (rating) => {
    // rating: 'helpful' | 'needs_improvement'
    if (feedbackSubmitting || feedbackRating) return; // 중복 방지
    setFeedbackError(null);
    if (!reportId && !duplicateReportId) {
      setFeedbackError(
        "리포트 ID를 확인할 수 없어 피드백을 제출할 수 없습니다.",
      );
      return;
    }
    const rid = reportId || duplicateReportId;
    try {
      setFeedbackSubmitting(true);
      // 낙관적 UI: 즉시 비활성화를 위한 상태 갱신
      setFeedbackRating(rating);
      setFeedbackDate(new Date().toISOString());
      // [중요] 운영에서 REACT_APP_API_URL 미주입 시 localhost로 호출되는 문제를 방지합니다.
      const apiUrl = process.env.REACT_APP_API_URL
        ? String(process.env.REACT_APP_API_URL).replace(/\/$/, "")
        : "";
      const res = await fetch(`${apiUrl}/api/v1/reports/${rid}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ rating }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(
          err.detail || `피드백 저장 실패 (status ${res.status})`,
        );
      }
      const data = await res.json();
      setFeedbackRating(data.feedback_rating || rating);
      setFeedbackDate(data.feedback_date || null);
    } catch (e) {
      const msg =
        e && e.message === "Failed to fetch"
          ? "네트워크 응답을 읽는 중 문제가 발생했습니다. 잠시 후 다시 확인해 주세요. 서버에는 저장되었을 수 있습니다."
          : e?.message || "피드백 저장 중 오류가 발생했습니다.";
      setFeedbackError(msg);
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const appTone = {
    surface: "#ffffff",
    surfaceMuted: "#f9fafb",
    border: "#e5e7eb",
    borderSoft: "#d1d5db",
    textStrong: "#111827",
    text: "#374151",
    textMuted: "#6b7280",
    primary: "#3498db",
    primarySoftBg: "#eff6ff",
    primarySoftBorder: "#bfdbfe",
  };

  // 앱 전용 인라인 컴포넌트
  const StepRow = ({ step, label, summary, description, tags }) => (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "14px 16px",
        borderRadius: 10,
        marginBottom: 10,
        border: `1px solid ${appTone.border}`,
        background: appTone.surface,
      }}
    >
      <span
        style={{
          width: 24,
          height: 24,
          borderRadius: 999,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          marginTop: 1,
          fontSize: 12,
          fontWeight: 700,
          color: appTone.primary,
          background: appTone.primarySoftBg,
          flexShrink: 0,
          border: `1px solid ${appTone.primarySoftBorder}`,
        }}
      >
        {step}
      </span>
      <div style={{ flex: 1, textAlign: "left" }}>
        <p
          style={{
            margin: 0,
            fontWeight: 700,
            fontSize: 14,
            color: appTone.textStrong,
          }}
        >
          {step}단계 {label}
        </p>
        {summary && (
          <p
            style={{
              margin: "4px 0 0",
              fontSize: 13,
              fontWeight: 600,
              color: appTone.text,
            }}
          >
            {summary}
          </p>
        )}
        {description && (
          <p
            style={{
              margin: "4px 0 0",
              fontSize: 12,
              color: appTone.textMuted,
              lineHeight: 1.6,
            }}
          >
            {description}
          </p>
        )}
        {tags && tags.length > 0 && (
          <div
            style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}
          >
            {tags.map((tag) => (
              <span
                key={tag}
                style={{
                  fontSize: 11,
                  padding: "2px 8px",
                  borderRadius: 20,
                  background: appTone.surfaceMuted,
                  color: appTone.text,
                  fontWeight: 600,
                  border: `1px solid ${appTone.border}`,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const formatCollectedAtDate = (iso, delimiter = ".") => {
    if (!iso) return "날짜 정보 없음";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "날짜 정보 없음";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}${delimiter}${month}${delimiter}${day}`;
  };

  // JSX 렌더링 부분
  return (
    <div className="page-header-center">
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 960,
          margin: "0 auto",
          textAlign: "center",
        }}
      >
        <div>
          <h1 className="app-title">나만의 AI 주치의</h1>
          <p className="app-description">
            {isApp
              ? "국민건강보험 의료 데이터를 기반으로 AI 맞춤 건강 리포트를 받아보세요."
              : "분석 방식을 선택하고, AI가 분석한 맞춤 건강 리포트를 받아보세요."}
          </p>
        </div>
        {/* 로그인/로그아웃 및 '내 리포트' 버튼은 헤더(Layout)에서 관리합니다. */}
      </div>

      <form onSubmit={handleSubmit} className="upload-form">
        {isApp ? (
          /* ── 앱 전용: 단계별 UI ── */
          <div style={{ width: "100%", maxWidth: 480, margin: "0 auto" }}>
            {(isLoadingCollectionStatus || isLoadingLatestReport) && (
              <p
                style={{ textAlign: "center", color: "#6b7280", fontSize: 14 }}
              >
                데이터 상태 확인 중...
              </p>
            )}
            {!isLoadingCollectionStatus && !isLoadingLatestReport && (
              <>
                {/* 연동 상태 박스 */}
                <div
                  style={{
                    border: `1px solid ${appTone.border}`,
                    borderRadius: 14,
                    padding: "16px 18px",
                    marginBottom: 16,
                    textAlign: "left",
                    background: appTone.surface,
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontSize: 16,
                      fontWeight: 700,
                      color: appTone.textStrong,
                    }}
                  >
                    {reportStatusTitle}
                  </p>
                  <p
                    style={{
                      margin: "6px 0 0",
                      fontSize: 13,
                      color: appTone.text,
                      lineHeight: 1.6,
                    }}
                  >
                    {reportStatusDescription}
                  </p>

                  <div
                    style={{
                      marginTop: 14,
                      display: "grid",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        background: appTone.surfaceMuted,
                        border: `1px solid ${appTone.border}`,
                        borderRadius: 12,
                        padding: "12px 14px",
                      }}
                    >
                      <p
                        style={{
                          margin: "0 0 8px",
                          fontSize: 13,
                          fontWeight: 700,
                          color: appTone.textStrong,
                        }}
                      >
                        분석 대상 데이터
                      </p>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 6,
                        }}
                      >
                        {["건강검진결과", "진료내역", "투약정보"].map((tag) => (
                          <span
                            key={tag}
                            style={{
                              fontSize: 12,
                              padding: "3px 10px",
                              borderRadius: 20,
                              background: appTone.surface,
                              color: appTone.text,
                              fontWeight: 600,
                              border: `1px solid ${appTone.border}`,
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      {reportStatus === "reported" &&
                        latestAppReport?.created_at && (
                          <p
                            style={{
                              margin: "10px 0 0",
                              fontSize: 12,
                              color: appTone.textMuted,
                              lineHeight: 1.5,
                            }}
                          >
                            리포트 생성일{" "}
                            <strong style={{ color: appTone.text }}>
                              {formatCollectedAtDate(
                                latestAppReport.created_at,
                                "-",
                              )}
                            </strong>
                          </p>
                        )}
                    </div>

                    <div
                      style={{
                        background: appTone.surfaceMuted,
                        border: `1px solid ${appTone.border}`,
                        borderRadius: 12,
                        padding: "12px 14px",
                      }}
                    >
                      <p
                        style={{
                          margin: "0 0 3px",
                          fontSize: 13,
                          fontWeight: 700,
                          color: appTone.textStrong,
                        }}
                      >
                        분석이 끝나면 바로 알려드릴게요
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: 12,
                          color: appTone.textMuted,
                          lineHeight: 1.5,
                        }}
                      >
                        보통 3~5분 정도 걸려요. 앱을 닫아도 계속 진행되고, 끝나면
                        푸시 알림으로 바로 확인할 수 있어요.
                      </p>
                    </div>
                  </div>

                  {reportStatus === "reported" && (
                    <div
                      style={{
                        marginTop: 14,
                        display: "flex",
                        gap: 8,
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                      }}
                    >
                      <button
                        type="button"
                        onClick={handleSecondaryAppCta}
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: appTone.text,
                          background: appTone.surfaceMuted,
                          border: `1px solid ${appTone.borderSoft}`,
                          borderRadius: 8,
                          padding: "8px 14px",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        검진 요약 보기
                      </button>
                      {latestReportId && (
                        <button
                          type="button"
                          onClick={handleLatestReportView}
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: appTone.text,
                            background: appTone.surfaceMuted,
                            border: `1px solid ${appTone.borderSoft}`,
                            borderRadius: 8,
                            padding: "8px 14px",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                        >
                          리포트 보기
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  disabled={isLoading || !authReady}
                  onClick={handlePrimaryAppCta}
                  className="submit-button"
                  style={{ marginBottom: 16 }}
                >
                  {primaryAppCtaLabel}
                </button>

                {/* 1단계: 국민건강보험공단 본인 인증 */}
                <StepRow
                  step={1}
                  label="본인 인증"
                  summary="간편인증으로 먼저 본인 확인"
                  description="데이터를 가져오기 전에 본인인지 확인해요. 카카오, PASS, 네이버, 토스 중 편한 방법으로 진행하면 돼요."
                />

                {/* 2단계: 의료 마이데이터 수집 */}
                <StepRow
                  step={2}
                  label="의료 마이데이터 수집"
                  summary="건강검진, 진료, 투약 정보를 차례대로 수집"
                  description="국민건강보험에서 필요한 의료 데이터를 바로 가져와요. 보통 1분 안쪽에서 끝나요."
                  tags={["건강검진결과", "진료내역", "투약정보"]}
                />

                {/* 3단계: AI 맞춤 건강 분석 */}
                <StepRow
                  step={3}
                  label="AI 맞춤 건강 분석"
                  summary="AI가 건강 리포트를 정리해 드려요"
                  description="수집된 데이터를 바탕으로 건강 상태를 읽기 쉽게 풀어주고, 이어서 실천 계획까지 제안해 줘요."
                />

                {/* 액션 버튼 */}
              </>
            )}
          </div>
        ) : (
          /* ── 웹: 기존 UI 그대로 ── */
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 16,
                marginBottom: 16,
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  name="analysis-mode"
                  value="pdf"
                  checked={analysisMode === "pdf"}
                  onChange={() => setAnalysisMode("pdf")}
                  disabled={isLoading || !authReady}
                />
                <span>PDF 파일로 분석하기</span>
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  name="analysis-mode"
                  value="direct"
                  checked={analysisMode === "direct"}
                  onChange={() => setAnalysisMode("direct")}
                  disabled={isLoading || !authReady}
                />
                <span>내 데이터로 분석하기</span>
              </label>
            </div>

            {analysisMode === "pdf" && (
              <div className="file-input-container">
                <input
                  type="file"
                  onChange={handleFileChange}
                  onClick={handleFileClick}
                  className="file-input hidden-file-input"
                  accept=".pdf,.txt,application/pdf,text/plain"
                  disabled={isLoading || !authReady}
                  id="file-upload"
                  capture={false}
                />
                <label htmlFor="file-upload" className="custom-file-upload">
                  {file ? file.name : "파일을 선택해 주세요!"}
                </label>
              </div>
            )}

            {analysisMode === "direct" && (
              <div
                style={{
                  border: `1px solid ${appTone.border}`,
                  borderRadius: 12,
                  marginBottom: 8,
                  textAlign: "left",
                  width: "100%",
                }}
              >
                {/* 섹션 1: 마이데이터 수집 */}
                {/* overflow:hidden 제거 — 모바일 native <select> 드롭다운 위치 오류 방지 */}
                <div
                  style={{
                    padding: "14px 16px",
                    background: appTone.surfaceMuted,
                    borderBottom: `1px solid ${appTone.border}`,
                    borderRadius: "12px 12px 0 0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontWeight: 700,
                          fontSize: 14,
                          color: appTone.textStrong,
                        }}
                      >
                        마이데이터 수집
                      </p>
                      <p
                        style={{
                          margin: "3px 0 0",
                          fontSize: 12,
                          color: appTone.textMuted,
                        }}
                      >
                        국민건강보험 건강검진 데이터를 가져옵니다.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        navigate(basePath + "/mydata/connect/consent")
                      }
                      disabled={isLoading || !authReady}
                      style={{
                        flexShrink: 0,
                        padding: "8px 14px",
                        borderRadius: 8,
                        border: `1px solid ${appTone.borderSoft}`,
                        background: appTone.surface,
                        color: appTone.text,
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: "pointer",
                      }}
                    >
                      + 가져오기
                    </button>
                  </div>
                </div>

                {/* 섹션 2: 가져온 데이터로 분석 */}
                <div
                  style={{
                    padding: "14px 16px",
                    background: "#fff",
                    borderRadius: "0 0 12px 12px",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 10px",
                      fontWeight: 700,
                      fontSize: 14,
                      color: appTone.textStrong,
                    }}
                  >
                    가져온 데이터로 분석
                  </p>
                  {isLoadingPatients && (
                    <div style={{ fontSize: 12, color: appTone.textMuted }}>
                      데이터 확인 중...
                    </div>
                  )}
                  {patientLoadError && (
                    <div style={{ fontSize: 12, color: "#c00" }}>
                      {patientLoadError}
                    </div>
                  )}
                  {!isLoadingPatients && !patientLoadError && patients.length === 0 && (
                    <div
                      style={{
                        fontSize: 13,
                        color: "#9ca3af",
                        textAlign: "center",
                        padding: "8px 0",
                      }}
                    >
                      아직 수집된 데이터가 없습니다. 위에서 데이터를 먼저 가져와
                      주세요.
                    </div>
                  )}
                  {!isLoadingPatients &&
                    patients.length === 1 &&
                    (() => {
                      const p = patients[0];
                      const name = p.name || p.patient_name || "분석 대상자";
                      const birthRaw = (p.birth_date || "").trim();
                      const birth = (() => {
                        if (!birthRaw) return "";
                        const parts = birthRaw.split("-");
                        if (parts.length === 3) {
                          const [y, m, d] = parts;
                          return `${y}년 ${m}월 ${d}일`;
                        }
                        return birthRaw;
                      })();
                      return (
                        <div
                          style={{
                            fontSize: 14,
                            color: "#111827",
                            fontWeight: 600,
                          }}
                        >
                          {birth ? `${name} (${birth})` : name}
                        </div>
                      );
                    })()}
                  {!isLoadingPatients &&
                    patients.length > 1 &&
                    (() => {
                      // 네이티브 <select>는 모바일에서 부모 CSS 컨텍스트에 따라
                      // 드롭다운이 좌상단에 잘못 렌더링되는 문제가 있어 커스텀으로 대체
                      const selectedPatient = patients.find(
                        (p) =>
                          String(p.patient_id || p.id || "") ===
                          selectedPatientId,
                      );
                      const getLabel = (p) => {
                        const name = p.name || p.patient_name || "분석 대상자";
                        const birthRaw = (p.birth_date || "").trim();
                        if (!birthRaw) return name;
                        const parts = birthRaw.split("-");
                        if (parts.length === 3) {
                          const [y, m, d] = parts;
                          return `${name} - ${y}년${m}월${d}일`;
                        }
                        return `${name} - ${birthRaw}`;
                      };
                      return (
                        <div style={{ position: "relative" }}>
                          <button
                            type="button"
                            disabled={isLoading || !authReady}
                            onClick={() => setPatientDropdownOpen((o) => !o)}
                            style={{
                              width: "100%",
                              padding: "8px 10px",
                              borderRadius: 6,
                              border: `1px solid ${patientDropdownOpen ? "#3b82f6" : "#d1d5db"}`,
                              background: "#fff",
                              fontSize: 14,
                              textAlign: "left",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              cursor: "pointer",
                              color: selectedPatient ? "#111827" : "#9ca3af",
                            }}
                          >
                            <span>
                              {selectedPatient
                                ? getLabel(selectedPatient)
                                : "건강분석 리포트가 필요하신 분을 선택해 주세요!"}
                            </span>
                            <span style={{ fontSize: 10, marginLeft: 8 }}>
                              {patientDropdownOpen ? "▲" : "▼"}
                            </span>
                          </button>
                          {patientDropdownOpen && (
                            <div
                              onMouseDown={(e) => e.stopPropagation()}
                              onTouchStart={(e) => e.stopPropagation()}
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                right: 0,
                                zIndex: 100,
                                background: "#fff",
                                border: "1px solid #d1d5db",
                                borderRadius: 6,
                                boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                                marginTop: 4,
                                overflow: "hidden",
                              }}
                            >
                              {patients.map((p) => {
                                const pid = String(p.patient_id || p.id || "");
                                const label = getLabel(p);
                                const isSelected = pid === selectedPatientId;
                                return (
                                  <div
                                    key={pid || label}
                                    onClick={() => {
                                      setSelectedPatientId(pid);
                                      setPatientDropdownOpen(false);
                                    }}
                                    style={{
                                      padding: "10px 12px",
                                      fontSize: 14,
                                      cursor: "pointer",
                                      background: isSelected
                                        ? "#eff6ff"
                                        : "#fff",
                                      color: isSelected ? "#1d4ed8" : "#111827",
                                      borderBottom: "1px solid #f3f4f6",
                                      fontWeight: isSelected ? 600 : 400,
                                    }}
                                  >
                                    {label}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={
                isLoading ||
                !authReady ||
                (analysisMode === "pdf" && !file) ||
                (analysisMode === "direct" && patients.length === 0) ||
                (analysisMode === "direct" &&
                  patients.length > 1 &&
                  !selectedPatientId)
              }
              className="submit-button"
            >
              {isLoading
                ? "AI가 분석 중입니다..."
                : analysisMode === "direct"
                  ? "분석 시작"
                  : "AI닥터큐, 설명해줘!"}
            </button>
          </>
        )}
      </form>

      {/**
       * [Why]
       * 로딩 단계에서도 결과 화면과 동일한 중앙 정렬 레이아웃을 유지하여 시각적 일관성을 확보합니다.
       * [How]
       * 최대 너비와 auto 마진을 가진 래퍼 div 안에 AnalysisProgress 컴포넌트를 배치합니다.
       */}
      {isLoading && (
        <div
          className="loading-container"
          style={{
            marginTop: 32,
            maxWidth: 800,
            marginLeft: "auto",
            marginRight: "auto",
            width: "100%",
          }}
        >
          <AnalysisProgress progress={progress} />
        </div>
      )}
      {/**
       * [Why]
       * 에러 메시지 역시 중앙 정렬 레이아웃으로 감싸 사용자에게 일관된 UI를 제공합니다.
       * [How]
       * 로딩 완료 후 에러가 존재하면 동일한 스타일의 래퍼로 감싸고, 메시지 자체는 좌측 정렬을 유지합니다.
       */}

      {/**
       * [Why]
       * 중복 파일이 감지된 경우 사용자에게 리포트 확인 또는 재분석 선택지를 제공하여 흐름을 제어합니다.
       * [How]
       * DuplicateFoundModal 컴포넌트를 렌더링하고 콜백에서 라우팅 및 재분석 로직을 수행합니다.
       */}
      <DuplicateFoundModal
        isOpen={isDuplicate}
        reportId={duplicateReportId}
        onClose={() => setIsDuplicate(false)}
        onViewReport={(rid) => navigate(`/reports/${rid}`)}
        onReAnalyze={async () => {
          setIsDuplicate(false);
          await startStream(true, pdfPasswordCached || undefined);
        }}
      />
      {passwordModalOpen && (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pdf-pass-title"
        >
          <div className="modal" style={{ maxWidth: 420 }}>
            <h3 id="pdf-pass-title">PDF 비밀번호가 필요합니다</h3>
            <p className="muted" style={{ marginTop: -6 }}>
              {pdfPasswordError ||
                "암호화된 PDF로 확인되었습니다. 비밀번호를 입력해 주세요."}
            </p>
            <input
              type="password"
              value={pdfPasswordInput}
              onChange={(e) => setPdfPasswordInput(e.target.value)}
              placeholder="비밀번호 입력"
              autoFocus
              style={{
                width: "80%",
                padding: "8px 10px",
                marginTop: 10,
                marginLeft: "auto",
                marginRight: "auto",
                boxSizing: "border-box",
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handlePasswordConfirm();
                }
              }}
            />
            <div
              className="modal-actions"
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                marginTop: 12,
              }}
            >
              <button
                type="button"
                className="btn-secondary"
                onClick={handlePasswordCancel}
              >
                취소
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handlePasswordConfirm}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 에러/알림 모달 추가 */}
      <MessageModal
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        title="알림"
        message={error}
        type="error"
      />

      {!isLoading && result && (
        <div
          className="result-container"
          style={{
            marginTop: 32,
            padding: 0,
            background: "transparent",
            border: "none",
            boxShadow: "none",
            maxWidth: 800,
            marginLeft: "auto",
            marginRight: "auto",
            textAlign: "left",
            width: "100%",
          }}
        >
          <div style={{ maxWidth: "100%", margin: 0 }}>
            <h2
              style={{ margin: "0 0 16px 0", fontSize: 24, textAlign: "left" }}
            >
              분석 리포트
            </h2>

            {summaryPreview && (
              <div
                style={{
                  background: "#f0f7ff",
                  padding: "12px 16px",
                  borderRadius: "10px",
                  marginBottom: 24,
                  border: "1px solid #dbeafe",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      background: "#e0e7ff",
                      color: "#3730a3",
                    }}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M3 3v18h18" />
                      <path d="M7 13l3 3 7-7" />
                    </svg>
                  </span>
                  <h3 style={{ margin: 0, color: "#1f2937", fontSize: 18 }}>
                    한눈에 보는 요약
                  </h3>
                </div>
                <p
                  style={{
                    margin: "8px 0 0 0",
                    lineHeight: 1.7,
                    whiteSpace: "pre-line",
                    color: "#1f2937",
                  }}
                >
                  {summaryPreview}
                </p>
              </div>
            )}

            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    background: "#dcfce7",
                    color: "#065f46",
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M4 4h16v16H4z" />
                    <path d="M8 8h8M8 12h8M8 16h6" />
                  </svg>
                </span>
                <h3 style={{ margin: 0, color: "#065f46", fontSize: 18 }}>
                  AI 상세 분석 결과
                </h3>
              </div>
              <div
                className="markdown-body"
                style={{
                  border: "1px solid #dbe2eb",
                  padding: 16,
                  borderRadius: 8,
                  backgroundColor: "#f5f7fa",
                }}
              >
                <ReactMarkdown
                  rehypePlugins={[
                    rehypeRaw,
                    [rehypeSanitize, markdownSanitizeOptions],
                  ]}
                >
                  {result}
                </ReactMarkdown>
              </div>
            </div>

            <div className="feedback-section" style={{ marginTop: 28 }}>
              <p>이 분석이 도움이 되셨나요?</p>
              <button
                className="feedback-button"
                onClick={() => handleFeedback("helpful")}
                disabled={feedbackSubmitting || !!feedbackRating}
                title={undefined}
              >
                👍 도움이 되었어요
              </button>
              <button
                className="feedback-button"
                onClick={() => handleFeedback("needs_improvement")}
                disabled={feedbackSubmitting || !!feedbackRating}
                title={undefined}
              >
                👎 개선이 필요해요
              </button>
              <div
                className="feedback-message"
                style={{ width: "100%", textAlign: "center", marginTop: 8 }}
              >
                {feedbackRating && (
                  <span
                    className="submitted-badge"
                    style={{ fontSize: 12, color: "#555" }}
                  >
                    {feedbackRating === "helpful"
                      ? "도움이 되셨다니 다행이네요. 감사합니다."
                      : "개선이 필요하다는 의견 감사합니다. 도움이 될 수 있도록 더욱 노력하겠습니다."}
                    {feedbackDate && (
                      <>
                        <br />
                        <span style={{ fontSize: 11, color: "#777" }}>
                          {new Date(feedbackDate).toLocaleString()}
                        </span>
                      </>
                    )}
                  </span>
                )}
                {feedbackError && (
                  <span
                    className="error-text"
                    style={{ color: "#c00", fontSize: 12 }}
                  >
                    {feedbackError}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MainPage;
